import React, { useState, useEffect, useMemo, useRef } from 'react';
import { setPlanner, setLiveView } from '../../state/campaignPlan';
import { getDerivedFromCriterion } from '../../state/campaignPlan';
import { useCampaignPlanVersion, getStateSnapshot, getHiresPerDay, getApplicantsPerDay, getMaxDailySpendCap } from '../../state/campaignPlan';
import DailySpendSlider from '../common/DailySpendSlider';
import { useCampaign } from '../../../../context/CampaignContext';
import { ChevronDown, Wand2 } from 'lucide-react';
import { optimizeGoal, optimizePerpetual, getOptimizationExplanation } from '../../../../services/CampaignOptimizer';

const AVAILABLE_JOBS = ["Server", "Cook", "Bartender", "Security", "Dishwasher", "Manager", "Cleaner", "Barista"];
const AVAILABLE_LOCATIONS = ["Downtown Seattle", "Bellevue Square", "Capitol Hill", "University District"];

type Zones = { lowRed: number; lowYellow: number; highYellow: number; highRed: number };

export default function CampaignBuilder({ 
  onOpenSources, 
  zones, 
  onZonesChange,
  availableRoles,
  availableLocations,
}: { 
  onOpenSources?: () => void;
  zones?: Zones;
  onZonesChange?: (z: Zones) => void;
  availableRoles?: string[];
  availableLocations?: string[];
} = {}) {
  const planVersion = useCampaignPlanVersion();
  const snapshot = getStateSnapshot();
  const planner0 = snapshot.planner || { startDate: null, endType: 'budget', endValue: null, dailySpend: 0 };
  const { selectedJobRole, selectedLocations, setCampaign } = useCampaign();
  
  // derive initial local states from global planner
  const initStart = planner0.startDate || new Date().toISOString().slice(0,10);
  const initDaily = planner0.dailySpend || 0;
  const initEndType = (planner0.endType as 'hires' | 'date' | 'budget') || 'budget';
  const initHires = initEndType === 'hires' ? Math.round(Number(planner0.endValue || 0)) : 25;
  const initBudget = initEndType === 'budget' ? Math.round(Number(planner0.endValue || 0)) : 7000;
  const initEndDate = (() => {
    if (initEndType !== 'date' || !planner0.endValue) return new Date(new Date(initStart).getTime() + 14*24*60*60*1000).toISOString().slice(0,10);
    const sd = new Date(initStart); sd.setDate(sd.getDate() + Math.round(Number(planner0.endValue)));
    return sd.toISOString().slice(0,10);
  })();
  
  // Job and Location state
  const [selectedRole, setSelectedRole] = useState<string>(
    selectedJobRole || AVAILABLE_JOBS[0]
  );
  const [selectedLocs, setSelectedLocs] = useState<string[]>(
    selectedLocations.length > 0 ? selectedLocations : [AVAILABLE_LOCATIONS[0]]
  );
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const [startDate, setStartDate] = useState(initStart);
  const [dailyBudget, setDailyBudget] = useState(initDaily);
  const [campaignType, setCampaignType] = useState<'goal' | 'perpetual'>((planner0 as any).campaignType || 'goal');
  const [endGoalType, setEndGoalType] = useState<'hires' | 'date' | 'budget'>(initEndType);
  const [hiresTarget, setHiresTarget] = useState(initHires);
  const [endDate, setEndDate] = useState(initEndDate);
  const [totalBudget, setTotalBudget] = useState(initBudget);
  const [liveView, setLive] = useState(snapshot.liveView);
  const [buildCampaignOpen, setBuildCampaignOpen] = useState(false);

  // Get coverage bounds from passed zones or localStorage fallback
  const getZoneBounds = useMemo(() => {
    if (zones) {
      return {
        min: zones.lowYellow,
        max: zones.highYellow
      };
    }
    // Fallback to localStorage if no zones passed
    try {
      const raw = localStorage.getItem('passcom-plan-zones');
      if (raw) {
        const z = JSON.parse(raw);
        return {
          min: Number(z?.lowYellow) || 95,
          max: Number(z?.highYellow) || 105
        };
      }
    } catch {}
    return { min: 95, max: 105 };
  }, [zones, planVersion]); // Re-read when zones or plan changes
  
  // Handler to update zones when coverage values change
  const handleCoverageChange = (field: 'min' | 'max', value: number) => {
    if (!zones || !onZonesChange) return;
    
    const newZones = { ...zones };
    if (field === 'min') {
      newZones.lowYellow = Math.max(0, Math.min(newZones.highYellow - 1, value));
    } else {
      newZones.highYellow = Math.max(newZones.lowYellow + 1, Math.min(200, value));
    }
    onZonesChange(newZones);
  };

  // Compute days from start to end for 'date' end criterion
  const daysFromStart = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const sd = new Date(startDate); sd.setHours(0,0,0,0);
    const ed = new Date(endDate); ed.setHours(0,0,0,0);
    return Math.max(0, Math.floor((ed.getTime() - sd.getTime()) / (1000*60*60*24)));
  }, [startDate, endDate]);

  useEffect(() => {
    setPlanner({ 
      startDate, 
      dailySpend: dailyBudget, 
      campaignType,
      endType: endGoalType, 
      endValue: endGoalType==='budget'? totalBudget : (endGoalType==='hires'? hiresTarget : daysFromStart)
    })
  }, [startDate, dailyBudget, campaignType, endGoalType, hiresTarget, endDate, totalBudget, daysFromStart]);

  useEffect(() => { setLiveView(liveView) }, [liveView]);

  // When global planner changes (e.g., user moves slider on Sources tab), reflect into local UI
  useEffect(() => {
    const s = getStateSnapshot().planner;
    if (!s) return;
    if (s.dailySpend !== dailyBudget) setDailyBudget(s.dailySpend || 0);
    if (s.startDate && s.startDate !== startDate) setStartDate(s.startDate);
    if ((s as any).campaignType !== campaignType) setCampaignType((s as any).campaignType || 'goal');
    if (s.endType && s.endType !== endGoalType) {
      setEndGoalType(s.endType as 'hires' | 'date' | 'budget');
    }
    if (s.endType === 'hires' && s.endValue != null && s.endValue !== hiresTarget) {
      setHiresTarget(Math.round(Number(s.endValue)));
    } else if (s.endType === 'budget' && s.endValue != null && s.endValue !== totalBudget) {
      setTotalBudget(Math.round(Number(s.endValue)));
    } else if (s.endType === 'date' && s.endValue != null) {
      const sd = new Date(s.startDate || startDate);
      sd.setDate(sd.getDate() + Math.round(Number(s.endValue)));
      const iso = sd.toISOString().slice(0,10);
      if (iso !== endDate) setEndDate(iso);
    }
  }, [planVersion]);

  // Compute slider cap based on active sources (mirrors Review panel allocator cap)
  const sliderMax = useMemo(() => {
    const cap = getMaxDailySpendCap();
    // No fallback; if cap is 0, the slider will remain at 0 and show guidance
    return Math.max(0, cap);
  }, [planVersion]);

  // Clamp dailyBudget when cap changes
  useEffect(() => {
    setDailyBudget((prev) => Math.min(prev, sliderMax));
  }, [sliderMax]);

  // Update context whenever job/location selections change
  useEffect(() => {
    if (selectedRole && selectedLocs.length > 0) {
      // Guard clause: only update if values actually changed
      const locationsMatch = 
        selectedLocs.length === selectedLocations.length &&
        selectedLocs.every((loc, idx) => loc === selectedLocations[idx]);
      const roleMatches = selectedRole === selectedJobRole;
      
      if (!locationsMatch || !roleMatches) {
        setCampaign(selectedLocs, selectedRole);
      }
    }
  }, [selectedRole, selectedLocs, selectedJobRole, selectedLocations, setCampaign]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setLocationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle location toggle
  const toggleLocation = (location: string) => {
    setSelectedLocs(prev => {
      if (prev.includes(location)) {
        if (prev.length === 1) return prev;
        return prev.filter(loc => loc !== location);
      } else {
        return [...prev, location];
      }
    });
  };

  // Format selected locations for display
  const getLocationDisplayText = () => {
    const effectiveLocations = availableLocations ?? AVAILABLE_LOCATIONS;
    if (selectedLocs.length === 0) return 'Select locations...';
    if (selectedLocs.length === 1) return selectedLocs[0];
    if (selectedLocs.length === effectiveLocations.length) return 'All locations';
    return `${selectedLocs.length} selected`;
  };

  const derived = getDerivedFromCriterion({
    startISO: startDate,
    endType: endGoalType,
    endValue: endGoalType==='date' ? daysFromStart : (endGoalType==='hires' ? hiresTarget : totalBudget),
    dailySpend: dailyBudget
  });

  const handleBuildCampaign = () => {
    const data = {
      startDate,
      dailyBudget,
      endGoalType,
      ...(endGoalType === 'hires' && { hiresTarget }),
      ...(endGoalType === 'date' && { endDate }),
      ...(endGoalType === 'budget' && { totalBudget })
    };
    
    console.log('Campaign Data:', data);
    alert(`Campaign Built!\n${JSON.stringify(data, null, 2)}`);
  };

  return (
    <div className="h-full overflow-auto bg-white max-w-[350px]">
      <div className="p-5 space-y-5">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold text-gray-800">Campaign Planner</h2>
          <div className="ml-auto">
            <button
              className="px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded shadow hover:bg-blue-700 flex flex-col items-center leading-tight"
              onClick={() => setBuildCampaignOpen(true)}
            >
              <span>Build</span>
              <span>Campaign</span>
            </button>
          </div>
        </div>
        
        {/* Job Role and Locations */}
        <div id="tour-location" className="space-y-3">
          {/* Job Role Selector */}
          <div className="flex items-center">
            <span className="text-gray-700 text-sm font-medium">Job Role</span>
            <div className="relative ml-auto w-[140px]">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-1.5 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm appearance-none bg-white"
              >
                {(availableRoles ?? AVAILABLE_JOBS).map((job) => (
                  <option key={job} value={job}>
                    {job}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* Location Selector (Multi Select Dropdown) */}
          <div className="flex items-center">
            <span className="text-gray-700 text-sm font-medium">Locations</span>
            <div className="relative ml-auto w-[140px]" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                className="w-full px-3 py-1.5 pr-8 border border-gray-300 rounded-lg text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:border-blue-500 flex items-center justify-between"
              >
                <span className={`flex-1 truncate ${selectedLocs.length === 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                  {getLocationDisplayText()}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform flex-shrink-0 absolute right-2.5 ${locationDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {locationDropdownOpen && (
                <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto" style={{ zIndex: 10000 }}>
                  <div className="py-1">
                    {(availableLocations ?? AVAILABLE_LOCATIONS).map((location) => (
                      <label
                        key={location}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocs.includes(location)}
                          onChange={() => toggleLocation(location)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{location}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Start Date + Daily Budget */}
        <div className="space-y-3" style={{ marginTop: '10px' }}>
          <div className="flex items-center">
            <span className="text-gray-700 text-sm font-medium">Start Date</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="ml-auto w-[140px] px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Daily Budget Slider with Auto button */}
          <div id="tour-budget">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-700 text-sm font-medium">Daily Budget</span>
              <div className="text-sm font-semibold text-gray-900">
                ${Math.round(Math.max(0, Number(getStateSnapshot().planner.dailySpend || 0)))}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex-1 min-w-0">
                <DailySpendSlider onOpenSources={onOpenSources} label="" variant="campaign" hideValue />
              </div>
              <button
                onClick={() => {
                  try {
                    let optimizedBudget = 0;
                    
                    if (campaignType === 'perpetual') {
                      // Perpetual mode: use zone bounds
                      optimizedBudget = optimizePerpetual(
                        getZoneBounds.min,
                        getZoneBounds.max,
                        100 // Default current headcount
                      );
                    } else {
                      // Goal mode: use end goal type
                      const goalType = endGoalType;
                      let goalValue: number | string = 0;
                      
                      if (goalType === 'date') {
                        goalValue = endDate;
                      } else if (goalType === 'hires') {
                        goalValue = hiresTarget;
                      } else if (goalType === 'budget') {
                        goalValue = totalBudget;
                      }
                      
                      optimizedBudget = optimizeGoal(goalType, goalValue, startDate);
                    }
                    
                    // Update the budget
                    setPlanner({ dailySpend: optimizedBudget });
                    
                    // Show feedback
                    const explanation = getOptimizationExplanation(
                      campaignType,
                      campaignType === 'goal' ? endGoalType : undefined,
                      optimizedBudget
                    );
                    alert(`Budget optimized to $${optimizedBudget}/day\n\n${explanation}`);
                  } catch (error) {
                    console.error('Optimization error:', error);
                    alert('Unable to optimize budget. Please check your campaign settings.');
                  }
                }}
                className="flex items-center gap-1 px-1.5 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors whitespace-nowrap"
                title="Auto-optimize daily budget based on campaign goals"
                id="tour-optimize"
              >
                <Wand2 size={12} />
                <span>auto</span>
              </button>
            </div>
          </div>

          {/* Applications/day */}
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>Applications/day</span>
            <span className="font-semibold">
              {(() => {
                const a = getApplicantsPerDay();
                return a >= 3 ? Math.round(a) : a.toFixed(1);
              })()}
            </span>
          </div>

          {/* Hires per day or week (dynamic label) */}
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>
              {(() => {
                const h = getHiresPerDay();
                return h < 1 ? 'Hires/week' : 'Hires/day';
              })()}
            </span>
            <span className="font-semibold">
              {(() => {
                const h = getHiresPerDay();
                const val = h < 1 ? (h * 7) : h;
                return val >= 3 ? Math.round(val) : val.toFixed(1);
              })()}
            </span>
          </div>
        </div>

        {/* Campaign End Type: Goal or Perpetual */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2.5">Campaign End</label>
          <div className="flex items-center gap-4 mb-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="campaignType"
                checked={campaignType === 'goal'}
                onChange={() => setCampaignType('goal')}
                className="w-4 h-4"
              />
              <span className="text-sm">Goal</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="campaignType"
                checked={campaignType === 'perpetual'}
                onChange={() => setCampaignType('perpetual')}
                className="w-4 h-4"
              />
              <span className="text-sm">Perpetual</span>
            </label>
          </div>

          {/* Goal Mode: Show End Goal Options */}
          {campaignType === 'goal' && (
            <div className="space-y-2.5">
              {/* Hires Option */}
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="hires"
                  name="endGoal"
                  checked={endGoalType === 'hires'}
                  onChange={() => {
                    const newH = Math.max(0, Math.round(derived.hires || 0));
                    setHiresTarget(newH);
                    setEndGoalType('hires');
                    setPlanner({ endType: 'hires', endValue: newH });
                  }}
                  className="w-4 h-4 cursor-pointer"
                />
                <label htmlFor="hires" className="text-gray-700 text-sm cursor-pointer">Hires</label>
                <input
                  type={endGoalType === 'hires' ? 'number' : 'text'}
                  value={endGoalType === 'hires' ? hiresTarget : Math.round(derived.hires)}
                  onChange={(e) => setHiresTarget(Number(e.target.value))}
                  readOnly={endGoalType !== 'hires'}
                  className={`ml-auto w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-right text-sm font-semibold focus:outline-none focus:border-blue-500 ${endGoalType !== 'hires' ? 'bg-slate-100 text-blue-600' : ''}`}
                  style={endGoalType !== 'hires' ? { backgroundColor: '#f1f5f9', color: '#2563eb' } : undefined}
                />
              </div>

              {/* Date Option */}
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="date"
                  name="endGoal"
                  checked={endGoalType === 'date'}
                  onChange={() => {
                    const newEndDate = derived.endDate || endDate;
                    // compute days from start to newEndDate for planner endValue
                    const sd = new Date(startDate); sd.setHours(0,0,0,0);
                    const ed = new Date(newEndDate); ed.setHours(0,0,0,0);
                    const newDays = Math.max(0, Math.floor((ed.getTime()-sd.getTime())/(1000*60*60*24)));
                    setEndDate(newEndDate);
                    setEndGoalType('date');
                    setPlanner({ endType: 'date', endValue: newDays });
                  }}
                  className="w-4 h-4 cursor-pointer"
                />
                <label htmlFor="date" className="text-gray-700 text-sm cursor-pointer">Date</label>
                <input
                  type={endGoalType === 'date' ? 'date' : 'text'}
                  value={endGoalType === 'date' ? endDate : (derived.endDate || '')}
                  onChange={(e) => setEndDate(e.target.value)}
                  readOnly={endGoalType !== 'date'}
                  className={`ml-auto w-[140px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:border-blue-500 ${endGoalType !== 'date' ? 'bg-slate-100 text-blue-600' : ''}`}
                  style={endGoalType !== 'date' ? { backgroundColor: '#f1f5f9', color: '#2563eb' } : undefined}
                />
              </div>

              {/* Budget Option */}
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="budget"
                  name="endGoal"
                  checked={endGoalType === 'budget'}
                  onChange={() => {
                    const newBudget = Math.max(0, Math.round(derived.budget || totalBudget || 0));
                    setTotalBudget(newBudget);
                    setEndGoalType('budget');
                    setPlanner({ endType: 'budget', endValue: newBudget });
                  }}
                  className="w-4 h-4 cursor-pointer"
                />
                <label htmlFor="budget" className="text-gray-700 text-sm cursor-pointer">Budget</label>
                <div className="relative ml-auto w-28">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type={endGoalType === 'budget' ? 'number' : 'text'}
                    value={endGoalType === 'budget' ? totalBudget : Math.round(derived.budget)}
                    onChange={(e) => setTotalBudget(Number(e.target.value))}
                    readOnly={endGoalType !== 'budget'}
                    className={`w-full pl-6 pr-3 py-1.5 border border-gray-300 rounded-lg text-right text-sm font-semibold focus:outline-none focus:border-blue-500 ${endGoalType !== 'budget' ? 'bg-slate-100 text-blue-600' : ''}`}
                    style={endGoalType !== 'budget' ? { backgroundColor: '#f1f5f9', color: '#2563eb' } : undefined}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Perpetual Mode: Show Coverage Range (synced with Zones) */}
          {campaignType === 'perpetual' && (
            <div className="space-y-2.5">
              <div className="flex items-center gap-3">
                <label className="text-gray-700 text-sm w-32">Minimum Coverage (%)</label>
                <input
                  type="number"
                  min={0}
                  max={getZoneBounds.max - 1}
                  step={5}
                  value={getZoneBounds.min}
                  onChange={(e) => handleCoverageChange('min', Number(e.target.value))}
                  className="ml-auto w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-right text-sm focus:outline-none focus:border-blue-500"
                  title="Good Zone lower bound"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-gray-700 text-sm w-32">Maximum Coverage (%)</label>
                <input
                  type="number"
                  min={getZoneBounds.min + 1}
                  max={200}
                  step={5}
                  value={getZoneBounds.max}
                  onChange={(e) => handleCoverageChange('max', Number(e.target.value))}
                  className="ml-auto w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-right text-sm focus:outline-none focus:border-blue-500"
                  title="Good Zone upper bound"
                />
              </div>
              <p className="text-xs text-gray-500 italic">
                Synced with Zones control
              </p>
            </div>
          )}
        </div>

        {/* Removed merge toggle and build button per request */}

        <style>{`
          input[type='range']::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }

          input[type='range']::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `}</style>
      </div>

      {/* Build Campaign Modal - Placeholder */}
      {buildCampaignOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setBuildCampaignOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Build Campaign</h3>
              <button
                onClick={() => setBuildCampaignOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="py-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-gray-600 text-sm">
                Campaign builder coming soon...
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setBuildCampaignOpen(false)}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

